/**
 * The signed-in cabinet's reads and writes on shows (SCRUM-587 S4): the list, one show, and the owner's
 * `PUT /shows/:id`. The routes are the server's own (`apps/api/src/shows/shows.controller.ts`); the API is the
 * gate — a stranger gets 404 on every one. The public show link is `show-links-api.ts`, not this.
 */
import type { Show, ShowState, ShowWhen } from "@scb/schema";
import { withPerformance, type NewPerformance } from "../shows/add-performance";
import { authFetch, parseOrThrow } from "./http";
import { sharedGet } from "./request-share";

/** `crew` — a worker the rental company hired (SCRUM-760): sees its show in the view of its role, no offers or money. */
export type ShowRole = "organiser" | "band" | "supplier" | "crew";

export interface VenueSummaryWire {
  id: string;
  name: string;
  city: string | null;
  timeZone: string;
}

/** `GET /shows` — one row per show the caller is a party of. */
export interface ShowListRow {
  id: string;
  title: string | null;
  when: ShowWhen;
  venue: VenueSummaryWire | null;
  performances: string[];
  state: ShowState;
  role: ShowRole;
  offers: { submitted: number; accepted: number };
  unread: boolean;
  archivedAt: string | null;
  version: number;
  /** The server-made per-rider sketch: no date, no venue yet. */
  isSketch: boolean;
  /** The agreed supplier's label (band, organiser) or the caller's own company (supplier); null = not agreed / «You». */
  rental: string | null;
  updatedAt: string;
}

/** One offer row on `GET /shows/:id` — a supplier is only ever sent its own. */
export interface ShowOfferRow {
  id: string;
  supplierUserId: string;
  supplierDisplayName: string | null;
  supplierCompany: string | null;
  /** For the organiser and the band; null for a supplier looking at its own row. */
  supplierEmail?: string | null;
  status: string;
  round: number;
  riderId: string;
  riderVersion: number;
  organiserState: string;
  technicalState: string;
  riderChanged: { answered: number; current: number } | null;
  /** Lines covered as asked (the green tick) and lines the supplier cannot supply. */
  matched: number;
  missing: number;
  /** Money does not exist yet: `null`, or absent. */
  price?: null;
  attachHow: string | null;
  /** The date and venue the supplier sent; `when` is `{ kind: "open", said: "not_set" }` for their explicit «not set yet»; null = never asked (legacy). */
  offered?: { when: unknown; venue: VenueSummaryWire | null } | null;
  updatedAt: string;
}

export interface ShowDetail {
  show: Show;
  version: number;
  role: ShowRole;
  roles: ShowRole[];
  state: ShowState;
  venue: VenueSummaryWire | null;
  offers: ShowOfferRow[];
  unread: boolean;
  isSketch: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Склеено на всплеск (SCRUM-887): список шоу на странице райдеров спрашивали ЧЕТЫРЕ раза подряд —
 *  значок входящих, вкладка инбокса и соседи, — и все четыре ответа были одинаковые. См.
 *  `api/request-share.ts`. */
export async function listShows(): Promise<ShowListRow[]> {
  return sharedGet("GET /shows", async () => parseOrThrow<ShowListRow[]>(await authFetch(`/shows`)));
}

export async function getShow(id: string): Promise<ShowDetail> {
  return parseOrThrow<ShowDetail>(await authFetch(`/shows/${encodeURIComponent(id)}`));
}

/**
 * One more act on a show (SCRUM-698 S1): reads the show, adds the performance at the end of the running order,
 * and writes the whole document back with the `version` just read. The caller owns the show; the server checks
 * that the rider, when given, is published at that version.
 */
export async function addPerformance(showId: string, act: NewPerformance): Promise<ShowDetail> {
  const detail = await getShow(showId);
  const { owner: _owner, schema_version: _schema, ...rest } = withPerformance(detail.show, act, crypto.randomUUID());
  const res = await authFetch(`/shows/${encodeURIComponent(showId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...rest, version: detail.version }),
  });
  return parseOrThrow<ShowDetail>(res);
}

/** `PUT /shows/:id` — the whole document plus the `version` last read (409 when someone else changed it). */
export async function putShow(detail: ShowDetail, patch: { when: ShowWhen; venue_id?: string }): Promise<ShowDetail> {
  // `owner` and `schema_version` are the server's to set; sending them is a 400.
  const { owner: _owner, schema_version: _schema, ...rest } = detail.show;
  const body = { ...rest, when: patch.when, ...(patch.venue_id ? { venue_id: patch.venue_id } : {}), version: detail.version };
  const res = await authFetch(`/shows/${encodeURIComponent(detail.show.uuid)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseOrThrow<ShowDetail>(res);
}
